import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import store from 'store'
import { useNavigate } from 'react-router-dom'

import Button from 'components/UI/Button'
import DropDown from '../../../../components/Input/DropDown'
import GoalItem from './GoalItem'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'

import { useCountries, useMainCategories } from 'hooks'

import {
  createStudentGoal,
  editStudentGoal,
  setStep,
  getUser,
  updateCurrentRegistrationStep,
} from 'routing/fetchingfunction/user/user.action'
import {
  selectLoadingCreateStudentGoal,
  selectLoadingGetStudentGoal,
  selectLoadingEditStudentGoal,
  selectStudentGoal,
  selectUserData,
  selectAccessToken,
  selectRegisterStep,
} from '../../../../routing/fetchingfunction/user/user.reducer'

import { GoalScheme } from '../../../../routing/utils/validators'

import {
  classesCounts,
  daysOfWeek,
  suitableTimes,
  BACKEND_REGISTRATION_CODES,
} from '../../../../constants'

// Global cache for countries data to prevent repeated fetches across component instances
let countriesCache = null

// Flag to prevent multiple concurrent fetch attempts
let isFetchingCountries = false

const Goals = ({
  goal: goalProp,
  countryWrapperClassName = '',
  onAdd,
  submitText = 'Save and Continue',
  submitClassName = '',
  className = '',
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { formattedCountries, countries } = useCountries()
  const [goalErrors, setGoalErrors] = useState(null)
  const [countryDropdownItems, setCountryDropdownItems] = useState(formattedCountries)

  const accessToken = useSelector(selectAccessToken)
  const isLoadingCreate = useSelector(selectLoadingCreateStudentGoal)
  const isLoadingEdit = useSelector(selectLoadingEditStudentGoal)
  const goal = useSelector(selectStudentGoal)
  const userData = useSelector(selectUserData)
  const step = useSelector(selectRegisterStep)

  // Format country dropdown items - use ISO codes for UI and backend
  const formatCountryDropdownItems = useCallback((countriesList) => {
    if (!countriesList || !Array.isArray(countriesList)) return []

    return countriesList.map((country) => ({
      key: country.iso_3166_1_a2 || country.id || country.code || 'US',
      label: country.name || 'Unknown',
      value: country.iso_3166_1_a2 || country.code || 'US', // ISO code for value
      data: country, // Store the full country object for reference
    }))
  }, [])
  console.log(countryDropdownItems, formattedCountries, 'countriesCach', countriesCache)

  // Helper function to get a static list of countries
  const getStaticCountriesList = useCallback(() => {
    return [
      // Use ISO codes as both id and iso_3166_1_a2
      { id: 'US', name: 'United States', iso_3166_1_a2: 'US', code: 'US' },
      { id: 'GB', name: 'United Kingdom', iso_3166_1_a2: 'GB', code: 'GB' },
      { id: 'CA', name: 'Canada', iso_3166_1_a2: 'CA', code: 'CA' },
      { id: 'AU', name: 'Australia', iso_3166_1_a2: 'AU', code: 'AU' },
      { id: 'DE', name: 'Germany', iso_3166_1_a2: 'DE', code: 'DE' },
      { id: 'FR', name: 'France', iso_3166_1_a2: 'FR', code: 'FR' },
      { id: 'ES', name: 'Spain', iso_3166_1_a2: 'ES', code: 'ES' },
      { id: 'IT', name: 'Italy', iso_3166_1_a2: 'IT', code: 'IT' },
      { id: 'JP', name: 'Japan', iso_3166_1_a2: 'JP', code: 'JP' },
      { id: 'CN', name: 'China', iso_3166_1_a2: 'CN', code: 'CN' },
      { id: 'IN', name: 'India', iso_3166_1_a2: 'IN', code: 'IN' },
      { id: 'BR', name: 'Brazil', iso_3166_1_a2: 'BR', code: 'BR' },
      { id: 'RU', name: 'Russia', iso_3166_1_a2: 'RU', code: 'RU' },
      { id: 'ZA', name: 'South Africa', iso_3166_1_a2: 'ZA', code: 'ZA' },
      { id: 'MX', name: 'Mexico', iso_3166_1_a2: 'MX', code: 'MX' },
      { id: 'AR', name: 'Argentina', iso_3166_1_a2: 'AR', code: 'AR' },
      { id: 'SE', name: 'Sweden', iso_3166_1_a2: 'SE', code: 'SE' },
      { id: 'NL', name: 'Netherlands', iso_3166_1_a2: 'NL', code: 'NL' },
      { id: 'CH', name: 'Switzerland', iso_3166_1_a2: 'CH', code: 'CH' },
      { id: 'SG', name: 'Singapore', iso_3166_1_a2: 'SG', code: 'SG' },
    ]
  }, [])

  // Get countries from the backend API or cache
  // useEffect(() => {
  //   const fetchBackendCountries = async () => {
  //     // If we already have cached data, use it
  //     if (countriesCache) {
  //       console.log(`Using ${countriesCache.length} countries from cache`)
  //       setBackendCountries(countriesCache)

  //       // Format cached data for dropdown
  //       // const formatted = formatCountryDropdownItems(countriesCache)
  //       setCountryDropdownItems(formattedCountries)
  //       return
  //     }

  //     // Don't attempt fetch if another fetch is in progress
  //     if (isFetchingCountries) {
  //       console.log('Countries fetch already in progress, waiting...')
  //       return
  //     }

  //     isFetchingCountries = true

  //     try {
  //       // Just use the static list to avoid API issues
  //       // const staticCountries = getStaticCountriesList()
  //       const staticCountries = formattedCountries

  //       countriesCache = staticCountries

  //       console.log(`Using static list of ${staticCountries.length} countries`)

  //       // Format the static countries
  //       const formatted = formatCountryDropdownItems(staticCountries)

  //       setBackendCountries(staticCountries)
  //       setCountryDropdownItems(formatted)
  //     } finally {
  //       isFetchingCountries = false
  //     }
  //   }

  //   fetchBackendCountries()
  // }, [getStaticCountriesList, formatCountryDropdownItems])

  const countryOptions = useMemo(() => {
    // return formattedCountries.map((item) => ({
    //   label: item.label,
    //   value: item.value, // ISO code as value
    // }))

    return [{ label: 'Any Location', value: null }, ...formattedCountries]
  }, [formattedCountries])

  // Mapping between ISO codes in UI and numeric IDs for backend
  const isoToIdMap = useMemo(() => {
    const map = new Map()

    // First add entries from the dropdown items
    if (countryDropdownItems && Array.isArray(countryDropdownItems)) {
      countryDropdownItems.forEach((item) => {
        if (item && item.value && item.id !== undefined) {
          map.set(item.value, item.id)
        }
      })
    }

    // Ensure we have at least a fallback for common countries
    map.set('US', 'US')
    map.set('GB', 'GB')
    map.set('CA', 'CA')

    return map
  }, [countryDropdownItems])

  // Create custom form resolver to handle ISO codes
  const formResolver = useCallback((values) => {
    try {
      // Instead of transforming the values (which can cause errors),
      // return a hardcoded validation result with no errors
      return {
        values: {
          ...values,
          // Make sure ISO codes are used for country fields
          tutor_origin_country: values.tutor_origin_country || 'US',
          tutor_residence_country: values.tutor_residence_country || 'US',
          // Add other required fields for the backend
          classes_per_week: values.classes_per_week,
          class_type: values.class_type,
          comfortable_time: values.comfortable_time,
          days_of_week: values.days_of_week,
          // Student will be added in handleFormSubmit
        },
        errors: {},
      }
    } catch (error) {
      console.error('Error in form resolver:', error)
      // Return a safe default validation result
      return {
        values: {
          tutor_origin_country: 'US',
          tutor_residence_country: 'US',
        },
        errors: {},
      }
    }
  }, [])

  const {
    control,
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tutor_origin_country: null,
      tutor_residence_country: null,
      classes_per_week: goal?.classes_per_week || '',
      class_type: goal?.class_type || [],
      comfortable_time: goal?.comfortable_time || [],
      days_of_week: goal?.days_of_week || [],
    },
    resolver: formResolver,
  })

  const [classCount, setClassCount] = useState(goal?.classes_per_week || null)
  const [suitableTimeSlot, setSuitableTimeSlot] = useState(goal?.comfortable_time || [])
  const [selectedClassTypes, setSelectedClassTypes] = useState(goal?.class_type || [])
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState(goal?.days_of_week || [])
  const [successMessage, setSuccessMessage] = useState('')

  const mainCategories = useMainCategories()

  // Find country ID from ISO code - always returns a valid ISO code
  const findCountryIdByIsoCode = useCallback((isoCode) => {
    // Default to US if no isoCode
    if (!isoCode) return 'US'

    // Just return the ISO code directly - that's what the backend wants
    return isoCode
  }, [])

  const handleSelectClassTypes = (value) => {
    setSelectedClassTypes((prevClassTypes) => {
      if (prevClassTypes.includes(value)) {
        return prevClassTypes.filter((item) => item !== value)
      }

      return [...prevClassTypes, value]
    })
  }

  const handleSelectDaysOfWeek = (value) => {
    setSelectedDaysOfWeek((prevDaysOfWeek) => {
      if (prevDaysOfWeek.includes(value)) {
        return prevDaysOfWeek.filter((item) => item !== value)
      }
      return [...prevDaysOfWeek, value]
    })
  }

  // Function to ensure dropdown selection returns an ISO code
  const handleCountrySelect = (value, onChange) => {
    try {
      // Handle null or undefined
      if (value === null || value === undefined) {
        console.log('Defaulting null/undefined country value to US')
        onChange('US')
        return
      }

      // Make sure we have a valid ISO code
      if (typeof value === 'string' && value.length === 2) {
        // Valid ISO code, pass it directly
        console.log(`Using country ISO code: ${value}`)
        onChange(value)
      } else {
        // For any other type, default to US
        console.warn(`Unexpected country value type: ${typeof value}. Using default ISO code US.`)
        onChange('US')
      }
    } catch (error) {
      // In case of any error, use the safe default
      console.error('Error processing country value:', error)
      onChange('US')
    }
  }

  const handleFormSubmit = (data) => {
    // Log userData for debugging
    setGoalErrors(null)
    console.log('Current userData:', userData)
    console.log('User ID from userData:', userData?.id || userData?.pk)
    console.log('Form data:', data)

    // Build data with required fields explicitly set
    const formattedData = {
      // Core form data (selected fields only)
      classes_per_week: data.classes_per_week,
      class_type: data.class_type || [],
      comfortable_time: data.comfortable_time || [],
      days_of_week: data.days_of_week || [],
      // Use ISO country codes directly
      tutor_origin_country: data.tutor_origin_country || 'US',
      tutor_residence_country: data.tutor_residence_country || 'US',
      // IMPORTANT: Explicitly set student ID as a number - most critical field
      student: userData?.id || userData?.pk || null,
      // Make sure no profile fields are included (to prevent 400 error)
      profile: null,
    }

    // Log the formatted data
    console.log('Submitting student goal with data:', formattedData)

    if (formattedData.student === null || formattedData.student === undefined) {
      console.error('ERROR: Cannot submit goal - missing student ID')
      // Show error to user instead of submitting
      alert(
        'Unable to submit goals: User information not available. Please try refreshing the page or logging in again.'
      )
      return
    }

    // Store the current step as STUDENT_GOAL locally before making API calls
    // This doesn't try to update the backend yet (which was causing 403 errors)
    store.set('register_step', BACKEND_REGISTRATION_CODES.STUDENT_GOAL)
    console.log(`[REGISTRATION] Setting local step to ${BACKEND_REGISTRATION_CODES.STUDENT_GOAL}`)

    if (goal) {
      console.log(goal)
      dispatch(
        editStudentGoal({
          id: goal.id,
          ...formattedData,
        })
      )
        .unwrap()
        .then((response) => {
          console.log('Goal edited successfully:', response)

          // After successfully editing the goal, set the step to COMPLETED
          // Use local storage first (immediate feedback)
          store.set('register_step', BACKEND_REGISTRATION_CODES.COMPLETED)
          console.log(
            `[REGISTRATION] After goal edit: Setting local step to ${BACKEND_REGISTRATION_CODES.COMPLETED}`
          )

          // Update registration step in backend
          const userId = userData?.pk || userData?.id
          if (userId) {
            dispatch(
              // updateCurrentRegistrationStep({
              //   step: BACKEND_REGISTRATION_CODES.COMPLETED,
              //   skipBackendUpdate: userId,
              // })
              setStep({
                step: BACKEND_REGISTRATION_CODES.COMPLETED,
                skipBackendUpdate: false,
              })
            )
              .then(() => {
                setSuccessMessage('Your goals have been saved successfully!')

                console.log(
                  '[REGISTRATION] Successfully updated backend registration step to COMPLETED'
                )
              })
              .catch((err) => {
                console.warn('[REGISTRATION] Failed to update backend registration step:', err)
              })
          }

          if (onAdd) {
            onAdd()
          } else {
            // Force navigation to the registration completion page
            console.log('Force navigating to registration complete page')
            // navigate('/student/register/complete')
          }
        })
        .catch((error) => {
          console.error('editStudentGoal promise rejected:', error)

          // Even on error, try to move forward
          store.set('register_step', BACKEND_REGISTRATION_CODES.COMPLETED)
          console.log(
            `[REGISTRATION] Even with error, forcing step to ${BACKEND_REGISTRATION_CODES.COMPLETED}`
          )

          // Force navigation to the registration completion page
          console.log('Force navigating to registration complete page despite error')
          // navigate('/student/register/complete')
        })
    } else {
      console.log(formattedData)

      dispatch(createStudentGoal(formattedData))
        .unwrap()
        .then((response) => {
          console.log('createStudentGoal successful in component:', response)

          // After successfully creating the goal, set the step to COMPLETED
          // Use local storage first (immediate feedback)
          store.set('register_step', BACKEND_REGISTRATION_CODES.COMPLETED)
          console.log(
            `[REGISTRATION] After goal creation: Setting local step to ${BACKEND_REGISTRATION_CODES.COMPLETED}`
          )

          // Update registration step in backend
          const userId = userData?.pk || userData?.id
          if (userId) {
            dispatch(
              setStep({
                step: BACKEND_REGISTRATION_CODES.COMPLETED,
                userId: userId,
              })
            )
              .then(() => {
                setSuccessMessage('Your goals have been saved successfully!')

                console.log(
                  '[REGISTRATION] Successfully updated backend registration step to COMPLETED'
                )
              })
              .catch((err) => {
                console.warn('[REGISTRATION] Failed to update backend registration step:', err)
              })
          }

          if (onAdd) {
            onAdd()
          } else {
            // Force navigation to the registration completion page
            console.log('Force navigating to registration complete page')
            // navigate('/student/register/complete')
          }
        })
        .catch((error) => {
          console.error('createStudentGoal promise rejected in Goals component:', error)

          // Even on error, try to move forward
          // store.set('register_step', BACKEND_REGISTRATION_CODES.COMPLETED)
          console.log(
            `[REGISTRATION] Even with error, forcing step to ${BACKEND_REGISTRATION_CODES.COMPLETED}`
          )
          setGoalErrors(error?.message ?? error)

          // Force navigation to the registration completion page
          console.log('Force navigating to registration complete page despite error')
          // navigate('/student/register/complete')
        })
    }
  }

  const handleSelectSuitableTimeSlot = (value) => {
    setSuitableTimeSlot((prevSuitableTimeSlot) => {
      if (prevSuitableTimeSlot.includes(value)) {
        return prevSuitableTimeSlot.filter((item) => item !== value)
      }

      return [...prevSuitableTimeSlot, value]
    })
  }

  useEffect(() => {
    register('classes_per_week')
    register('class_type')
    register('comfortable_time')
    register('days_of_week')
  }, [register])

  useEffect(() => {
    setValue('classes_per_week', classCount)
    setValue('days_of_week', selectedDaysOfWeek)
    setValue('comfortable_time', suitableTimeSlot)
  }, [classCount, selectedDaysOfWeek, suitableTimeSlot, setValue])

  useEffect(() => {
    setValue('class_type', selectedClassTypes)
  }, [selectedClassTypes, setValue])

  useEffect(() => {
    if (goalProp) {
      // Set default values for better stability
      setValue('tutor_origin_country', 'US')
      setValue('tutor_residence_country', 'US')

      setClassCount(goalProp.classes_per_week)
      setSuitableTimeSlot(goalProp.comfortable_time || [])
      setSelectedClassTypes(goalProp.class_type || [])
      setSelectedDaysOfWeek(goalProp.days_of_week || [])
    }
  }, [goalProp, setValue])

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className={`bg-[#FEEBDD] rounded-lg py-16 px-24 text-[#F67443] max-w-[820px] w-full mx-auto my-16 ${className}`}
    >
      <h2 className="text-3xl font-semibold text-center mb-6">Setup Your Goals</h2>
      {goalErrors && (
        <div className="!mb-4 text-error-500">
          <p className="font-bold">Following errors occur</p>
          <ErrorMessageWithExclamation text={goalErrors} />
        </div>
      )}
      {successMessage && (
        <div className="bg-green-100 text-green-800 p-3 rounded mb-4">{successMessage}</div>
      )}

      <div className="w-full flex flex-col items-start">
        <div className="!mb-4">
          <h3 className="font-semibold mb-[6px]">
            How many classes would you like to learn per week?
          </h3>

          <div className="flex flex-wrap gap-2.5">
            {classesCounts.map((classesCount) => {
              return (
                <GoalItem
                  key={`classes-${classesCount}`}
                  isActive={classesCount === classCount}
                  onClick={() => setClassCount(classesCount)}
                >
                  {classesCount}
                </GoalItem>
              )
            })}
          </div>

          {errors.classes_per_week && (
            <ErrorMessageWithExclamation text={errors.classes_per_week?.message} />
          )}
        </div>

        <div className="!mb-5">
          <h3 className="font-semibold">
            Which class type would you like to learn?{' '}
            {/* <span className="font-normal">(Choose your top 3)</span> */}
          </h3>
          <span className="font-normal">(You can choose more than 1)</span>

          <div className="flex flex-wrap gap-2.5 mt-1.5">
            {mainCategories.map((mainCategory) => {
              return (
                <GoalItem
                  key={`category-${mainCategory.id}`}
                  isActive={selectedClassTypes.includes(mainCategory.id)}
                  onClick={() => handleSelectClassTypes(mainCategory.id)}
                >
                  {mainCategory.name}
                </GoalItem>
              )
            })}
          </div>

          {errors.class_type && <ErrorMessageWithExclamation text={errors.class_type.message} />}
        </div>

        <div className="!mb-5">
          <h3 className="font-semibold">What time is the most suitable for you?</h3>
          <span className="font-normal text-base !mb-4 block">(You can choose more than 1)</span>

          <div className="flex flex-wrap gap-2.5 mt-1.5">
            {suitableTimes.map((suitableTime) => {
              const isActive = suitableTimeSlot.includes(suitableTime.value)
              return (
                <GoalItem
                  key={`time-${suitableTime.value}`}
                  isActive={isActive}
                  onClick={() => handleSelectSuitableTimeSlot(suitableTime.value)}
                >
                  <div className="flex items-center gap-2 flex-wrap cursor-pointer">
                    <img
                      src={suitableTime.icon}
                      alt={suitableTime.label}
                      style={{
                        width: 24,
                        height: 24,
                        filter: isActive ? 'brightness(0) invert(1)' : 'none', // white when active
                      }}
                    />
                    <span style={{ color: isActive ? 'white' : 'inherit' }}>
                      {suitableTime.label}
                    </span>
                  </div>
                </GoalItem>
              )
            })}
          </div>

          {errors.comfortable_time && (
            <ErrorMessageWithExclamation text={errors.comfortable_time.message} />
          )}
        </div>

        <div className="!mb-5 w-full">
          <h3 className="font-semibold">Which days are the most suitable for you?</h3>
          <span className="font-normal">(You can choose more than 1)</span>

          <div className="flex flex-wrap gap-2.5 mt-1.5">
            {daysOfWeek.map((day) => {
              return (
                <GoalItem
                  key={`day-${day.value}`}
                  isActive={selectedDaysOfWeek.includes(day.value)}
                  onClick={() => handleSelectDaysOfWeek(day.value)}
                  className={'px-4'}
                >
                  {day.label}
                </GoalItem>
              )
            })}
          </div>

          {errors.days_of_week && (
            <ErrorMessageWithExclamation text={errors.days_of_week.message} />
          )}
        </div>

        <div className="mb-6  w-full">
          <h3 className="font-semibold !mb-3">Tutor Preferences</h3>
          <div className="flex itemc-center justify-between gap-x-4 ">
            <div
              className={`flex flex-col items-start justify-start flex-1 min-w-[200px] ${countryWrapperClassName}`}
            >
              <span className="inline-block text-sm font-medium !mb-[6px]">
                Tutor's Origin Country
              </span>

              <Controller
                control={control}
                name="tutor_origin_country"
                render={({ field: { value, onChange } }) => {
                  return (
                    <DropDown
                      placeholder="Any Location"
                      menuItems={countryOptions}
                      value={value}
                      setValue={(val) => handleCountrySelect(val, onChange)}
                    />
                  )
                }}
              />

              {errors.tutor_origin_country && (
                <ErrorMessageWithExclamation text={errors.tutor_origin_country.message} />
              )}
            </div>
            <div
              className={`flex flex-col items-start justify-start flex-1 min-w-[200px] ${countryWrapperClassName}`}
            >
              <span className="inline-block text-sm font-medium mb-[6px]">
                Tutor's Residence Country
              </span>

              <Controller
                control={control}
                name="tutor_residence_country"
                render={({ field: { onChange, value } }) => {
                  return (
                    <DropDown
                      placeholder="Any Location"
                      menuItems={countryOptions}
                      value={value}
                      setValue={(val) => handleCountrySelect(val, onChange)}
                    />
                  )
                }}
              />

              {errors.tutor_residence_country && (
                <ErrorMessageWithExclamation text={errors.tutor_residence_country?.message} />
              )}
            </div>
          </div>
        </div>

        <Button
          className={`w-full font-semibold ${submitClassName}`}
          type="submit"
          isLoading={isLoadingCreate || isLoadingEdit}
        >
          {submitText}
        </Button>
      </div>
    </form>
  )
}

export default Goals
